/**
 * Post utility functions
 * Shared utilities for processing LinkedIn posts
 */

const DEFAULT_BLOG_IMAGE =
  "https://res.cloudinary.com/dhbe6wtod/image/upload/v1765447676/werkix/blog/Join_us_if_you_thrive_for_IT._1_kdjbva.png";

/**
 * Normalize post media from Posts API (images[]) or legacy contentEntities.
 * @param {Object} post - LinkedIn post from API
 * @returns {Array<{url: string, altText: string}>}
 */
export const getPostImages = (post) => {
  if (!post) return [];

  if (Array.isArray(post.images) && post.images.length > 0) {
    return post.images
      .filter((img) => img?.url)
      .map((img, index) => ({
        url: img.url,
        altText: img.altText || `Werkix LinkedIn post image ${index + 1}`,
      }));
  }

  const entities = post.content?.contentEntities;
  if (!Array.isArray(entities) || entities.length === 0) {
    return [];
  }

  return entities
    .map((entity, index) => {
      const url =
        entity?.entityLocation ||
        entity?.thumbnails?.[0]?.resolvedUrl ||
        null;
      if (!url) return null;
      return {
        url,
        altText: entity?.description || `Werkix LinkedIn post image ${index + 1}`,
      };
    })
    .filter(Boolean);
};

/**
 * @param {Object} post
 * @returns {{ urn?: string, url: string, thumbnailUrl?: string, title?: string } | null}
 */
export const getPostVideo = (post) => {
  if (!post?.video?.url) return null;
  return {
    urn: post.video.urn,
    url: post.video.url,
    thumbnailUrl: post.video.thumbnailUrl || null,
    title: post.video.title || "",
  };
};

/**
 * @param {Object} post
 * @returns {boolean}
 */
export const isVideoPost = (post) => {
  return post?.contentType === "video" || Boolean(getPostVideo(post));
};

/**
 * @param {Object} post
 * @returns {Array<{ type: 'image' | 'video', url: string, poster?: string, altText?: string }>}
 */
export const getPostMedia = (post) => {
  const images = getPostImages(post).map((img) => ({
    type: "image",
    url: img.url,
    altText: img.altText,
  }));

  const video = getPostVideo(post);
  if (video) {
    images.push({
      type: "video",
      url: video.url,
      poster: video.thumbnailUrl || undefined,
      altText: video.title || "Werkix LinkedIn video post",
    });
  }

  return images;
};

/**
 * @param {Object} post
 * @param {string} [fallbackUrl]
 * @returns {string}
 */
export const getPostPrimaryImage = (post, fallbackUrl = DEFAULT_BLOG_IMAGE) => {
  const firstImage = getPostImages(post)[0]?.url;
  if (firstImage) return firstImage;

  const videoThumbnail = getPostVideo(post)?.thumbnailUrl;
  if (videoThumbnail) return videoThumbnail;

  return fallbackUrl;
};

/**
 * @param {Object} post
 * @returns {number}
 */
export const getPostImageCount = (post) => getPostImages(post).length;

/**
 * Formats a timestamp into DD/MM/YYYY format
 * @param {string|number} timestamp - Timestamp to format
 * @returns {string} Formatted date string
 */
export const getPostDate = (timestamp) => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${day}/${month}/${year}`;
};

/**
 * Extracts the first heading from text content
 * @param {string} text - Text content to extract heading from
 * @param {number} headingLength - Maximum length for the heading
 * @returns {string} Extracted heading
 */
export const extractHeading = (text, headingLength = 100) => {
  if (!text) return "No Title Available";
  const heading = text.split("\n\n")[0];
  return heading.length > headingLength
    ? `${heading.substring(0, headingLength)}...`
    : heading;
};

/**
 * Truncates text to a maximum length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length (default: 600)
 * @returns {string} Truncated text with ellipsis if needed
 */
export const checkTextLength = (text, maxLength = 600) => {
  if (text.length > maxLength) {
    return text.slice(0, maxLength) + "...";
  }
  return text;
};
